/* eslint-disable react/no-unused-prop-types */
import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import ActionMenu from 'src/components/layouts/ActionMenu/ActionMenu';
import { useOutsideClick } from 'src/hooks/utils';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Carrier } from 'src/services/saleOrder';
import styles from './CountryGroupCard.module.scss';
import Icon from '../Icon/Icon';
import Divider from '../Divider/Divider';
import { OptionItemProps, OptionItemTone } from '../OptionItem/OptionItem';
import Image from '../Image/Image';
import { useCountryGroupArchive } from '../../../services/countries';
import ConfirmationModal from '../../modals/ConfirmationModal/ConfirmationModal';

export interface CountryGroupCardProps {
  id?: number | null;
  name: string;
  description?: string | null;
  countries?: Array<any>;
  carriers?: Array<Carrier> | null;
  allowEdition?: boolean;
}

const CountryGroupCard: React.FC<CountryGroupCardProps> = ({ id, name, description, carriers, allowEdition }) => {
  const { t } = useTranslation();
  const [menuVisible, setMenuVisible] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const archiveMutation = useCountryGroupArchive();

  const toggleMenu = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    setMenuVisible(prevVisible => !prevVisible);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setMenuVisible(false);
    }
  };

  const onCountryGroupArchive = async (countryGroupId: number) => {
    try {
      await archiveMutation.mutateAsync({
        params: {
          path: {
            country_group_id: countryGroupId,
          },
        },
      });
      toast.success(t('success.country_group_archived'));
    } catch (error) {
      toast.error(t('errors.country_group_archive_failed'));
    }
  };

  useOutsideClick(menuRef, handleClickOutside);
  const navigate = useNavigate();

  const items = [
    {
      accessoryItem: 'edit',
      label: t('general.edit_group'),
      onClick: () => {
        setMenuVisible(false);
        navigate(`/carrier-analytics/countries-and-groups/edit/${id}`);
      },
    },
    {
      accessoryItem: 'delete',
      label: 'Archivieren',
      onClick: () => {
        setMenuVisible(false);
        setIsConfirmationOpen(true);
      },
      tone: OptionItemTone.Destructive,
      isError: true,
    },
  ] as OptionItemProps[];

  const handleConfirmDelete = async (confirmed: boolean) => {
    if (confirmed) {
      await onCountryGroupArchive(Number(id));
      setIsConfirmationOpen(false);
    } else {
      setIsConfirmationOpen(false);
    }
  };

  return (
    <div className={classNames(styles.root)}>
      <div className={styles.content}>
        <div className="px-20 flex flex-col">
          <div className="flex justify-between relative">
            <div className={styles.iconBox}>
              <Icon name="public" width={28} height={28} />
            </div>
            {allowEdition && (
              <div ref={buttonRef} onClick={toggleMenu} role="presentation" onKeyDown={toggleMenu}>
                <Icon name="settings" className={`${menuVisible ? 'settingsOpen' : 'text-faded'} cursor-pointer`} />
              </div>
            )}
          </div>
          <div className="flex gap-x-4 mt-16">
            <h3 className="typo-text-16--bold text-full">{name}</h3>
          </div>
          <span className="typo-text-12 text-faded mt-4 text-ellipsis line-clamp-2">{description}</span>
        </div>
        <div className="px-20">
          <Divider customColor="border-subtle" className="my-16" />
          <div className="typo-text-12 status-neutral-4-full flex items-center gap-x-20 gap-y-12 flex-wrap">
            {carriers?.map(carrier => (
              <div key={carrier.id} className="flex items-center gap-x-8">
                <Image
                  imageUrl={carrier.logo_url || ''}
                  alt={`Logo ${carrier.name}`}
                  className="w-7 h-7 rounded-xs"
                  fallbackComponent={<Icon name="package" width={20} height={20} />}
                />
                <span className="text-ellipsis line-clamp-1 text-faded">{carrier.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {menuVisible && (
        <div className={styles.actionMenu} ref={menuRef}>
          <ActionMenu items={items} />
        </div>
      )}
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        title={t('general.archive_group')}
        description={t('general.archive_group_text')}
        onClose={handleConfirmDelete}
      />
    </div>
  );
};

export default CountryGroupCard;
